import { NgTemplateOutlet } from "@angular/common";
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import moment from "moment";

import { AdaaHelper } from "../../../../core/utils";
import { WfDifferenceComponent } from "../../../../shared/components";
import { isTransformationalAgreement } from "../../../../shared/components/modals/workflows/wf-difference/wf-difference.utils";
import { WorkflowProcessCtl } from "../../../../shared/models";
import { NotificationCommunication } from "../../../../shared/models/notification-center-model";
import { canReviewNotification, notificationReviewInput } from "../../../notification-center/notification-center.utils";
import { Adaa4NotificationsService } from "../../services/adaa4-notifications-service";
import { Adaa4StepperComponent } from "../adaa4-stepper/adaa4-stepper";

@Component({
  selector: "adaa-adaa4-notification-details",
  imports: [TranslatePipe, RouterLink, NgTemplateOutlet, WfDifferenceComponent, Adaa4StepperComponent],
  templateUrl: "./adaa4-notification-details.html",
  styleUrl: "./adaa4-notification-details.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Adaa4NotificationDetailsComponent implements OnInit {
  private readonly _notificationsService = inject(Adaa4NotificationsService);

  readonly AdaaHelper = AdaaHelper;
  readonly moment = moment;

  id = input.required<string>();

  notification = signal<NotificationCommunication | undefined>(undefined);
  loaded = signal(false);
  history = signal<WorkflowProcessCtl[]>([]);
  workflowDifference = viewChild.required<WfDifferenceComponent>("workflowDifference");

  actionType = computed(() => {
    const notification = this.notification();
    return notification ? this._notificationsService.actionType(notification) : "other";
  });
  isPending = computed(() => {
    const notification = this.notification();
    return !!notification && this._notificationsService.isPending(notification);
  });
  canReview = computed(() => canReviewNotification(this.notification()));
  isAgreementFile = computed(() => isTransformationalAgreement(this.notification()?.itemType));
  showsDifferences = computed(() => this.canReview() && !this.isAgreementFile());
  canShowOnlyDifferences = computed(() => ["update", "dataEntry"].includes(this.actionType()));
  pendingDays = computed(() => {
    const notification = this.notification();
    return notification ? this._notificationsService.pendingDays(notification) : 0;
  });
  pendingTone = computed(() => {
    const days = this.pendingDays();
    if (days >= 14) return "late";
    if (days >= 7) return "warn";
    return "ok";
  });

  constructor() {
    afterRenderEffect(() => {
      const notification = this.notification();
      if (!notification || !this.showsDifferences()) return;

      untracked(() => this.workflowDifference().load(notificationReviewInput(notification)));
    });
  }

  public ngOnInit(): void {
    this._notificationsService.open(+this.id()).subscribe((notification) => {
      this.notification.set(notification);
      this.loaded.set(true);
      if (notification) this._loadHistory(notification);
    });
  }

  public downloadAgreement(): void {
    const notification = this.notification();
    if (!notification) return;

    this.workflowDifference().load(notificationReviewInput(notification));
  }

  private _loadHistory(notification: NotificationCommunication): void {
    this._notificationsService.history(notification).subscribe((rows) => this.history.set(rows));
  }
}
